import { memo, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { Pagination } from '@mui/material'
import { LinearProgress } from '@material-ui/core'
import classNames from 'classnames'
import SectionWrapper from '../SectionWrapper'
import styles from './styles.module.scss'

const PaginatedList = memo(
  ({
    // Data
    items = [],
    pagination = {},
    isLoading = false,

    // Configuration
    title,
    titleId,
    emptyMessageId = 'community.tournaments.noMore',
    itemsPerPage = 10,

    // Rendering
    renderItem,
    itemKey = 'uuid',

    // Events
    onPageChange,

    // Styling
    className,
    listClassName,
    itemClassName,

    // Other props
    ...props
  }) => {
    const hasItems = useMemo(() => items?.length > 0, [items?.length])

    const shouldShowPagination = useMemo(() => {
      return (
        hasItems && (items.length > itemsPerPage || pagination?.hasNextPage)
      )
    }, [hasItems, items.length, itemsPerPage, pagination?.hasNextPage])

    const paginationProps = useMemo(
      () => ({
        classes: { ul: styles.pagination },
        count: pagination?.lastPage,
        page: pagination?.currentPage,
        onChange: onPageChange,
        variant: 'outlined',
      }),
      [pagination?.lastPage, pagination?.currentPage, onPageChange]
    )

    const renderedItems = useMemo(() => {
      if (!hasItems) return null

      return items.map((item, index) => {
        const key = item[itemKey] || `${itemKey}-${index}`

        return (
          <li key={key} className={itemClassName}>
            {renderItem ? renderItem(item, index) : item}
          </li>
        )
      })
    }, [items, hasItems, renderItem, itemKey, itemClassName])

    const renderContent = useMemo(() => {
      if (isLoading) {
        return <LinearProgress />
      }

      if (!hasItems) {
        return (
          <div>
            <FormattedMessage id={emptyMessageId} />
          </div>
        )
      }

      return (
        <article>
          <ul className={classNames(styles.list, listClassName)}>
            {renderedItems}
            {shouldShowPagination && (
              <div className={styles.paginationWrapper}>
                <Pagination {...paginationProps} />
              </div>
            )}
          </ul>
        </article>
      )
    }, [
      isLoading,
      hasItems,
      emptyMessageId,
      renderedItems,
      shouldShowPagination,
      paginationProps,
      listClassName,
    ])

    return (
      <SectionWrapper
        className={className}
        title={title}
        titleId={titleId}
        {...props}
      >
        <section className={styles.cards}>{renderContent}</section>
      </SectionWrapper>
    )
  }
)

export default PaginatedList
